/**
 * Pages Portail Corporate — Phase 101cx-corp.
 *
 * Bloc regroupé pour rester compact (contrairement à ShopAuth.jsx qui est
 * énorme). Exporte : CorporatePortalLogin, CorporatePortalActivate,
 * CorporatePortalForgot, CorporatePortalReset, CorporatePortal.
 *
 * Design : palette Maison Thaï (vert olive #1F4E3D + accents doré #8C6B2F),
 * pas d'AuthShell partagé pour rester léger — layout inline dans chaque page.
 */
import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Leaf, Mail, Lock, Loader2, CheckCircle2, LogOut, Building2, TrendingUp, TrendingDown, ArrowRight, FileText, Calendar, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useCorporateAuth, corporateApi, formatCorpErrorDetail } from "../lib/corporateAuth";

const fmtCHF = (n) => `${Number(n || 0).toFixed(2)} CHF`;

// ============== Shell partagé (login / activate / forgot / reset) ==============
function AuthShell({ title, subtitle, children, testid }) {
  return (
    <div
      className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-4 relative overflow-hidden"
      data-testid={testid}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#F5F5F0] via-[#EFEEDF] to-[#E0E5DC]" aria-hidden="true" />
      <div className="absolute top-0 right-0 w-[60%] h-[60%] bg-[#1F4E3D]/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/4" aria-hidden="true" />
      <div className="absolute bottom-0 left-0 w-[40%] h-[40%] bg-[#8C6B2F]/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" aria-hidden="true" />

      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-11 h-11 rounded-lg bg-[#1F4E3D] flex items-center justify-center text-white shadow-sm">
            <Leaf className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading text-xl font-medium text-[#1A1C1A]">Espace Corporate</div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#575A57]">Maison Thaï</div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E5E0] rounded-xl p-7 shadow-[0_8px_30px_rgba(31,78,61,0.08)]">
          <h1 className="font-heading text-2xl font-medium text-[#1A1C1A]">{title}</h1>
          {subtitle && <p className="text-sm text-[#575A57] mt-1 mb-6">{subtitle}</p>}
          <div className={subtitle ? "" : "mt-4"}>{children}</div>
        </div>
      </div>
    </div>
  );
}

function ErrorBox({ children, testid }) {
  return (
    <div
      className="bg-[#FCEEEA] border border-[#C05C47]/30 text-[#8B3D2D] text-sm rounded-md px-3 py-2"
      data-testid={testid}
    >
      {children}
    </div>
  );
}

// ============== Login ==============
export function CorporatePortalLogin() {
  const { contact, login } = useCorporateAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (contact && contact !== false) return <Navigate to="/portail-corporate" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(formatCorpErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Connexion"
      subtitle="Accédez à votre espace Corporate — tarifs employés et suivi CA."
      testid="corp-login-page"
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="corp-login-form">
        <div>
          <Label htmlFor="email" className="mb-1.5 block text-[#1A1C1A]">Adresse email</Label>
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#575A57] pointer-events-none" />
            <Input
              id="email" type="email" autoComplete="username"
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@entreprise.ch" required className="pl-9"
              data-testid="corp-login-email"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="password" className="mb-1.5 block text-[#1A1C1A]">Mot de passe</Label>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#575A57] pointer-events-none" />
            <Input
              id="password" type="password" autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              required className="pl-9"
              data-testid="corp-login-password"
            />
          </div>
        </div>
        {error && <ErrorBox testid="corp-login-error">{error}</ErrorBox>}
        <Button
          type="submit" disabled={submitting || !email || !password}
          className="w-full bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
          data-testid="corp-login-submit"
        >
          {submitting ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connexion…</>
          ) : "Se connecter"}
        </Button>
        <div className="text-center pt-2">
          <Link
            to="/portail-corporate/mot-de-passe-oublie"
            className="text-sm text-[#575A57] hover:text-[#1F4E3D] underline"
            data-testid="corp-login-forgot-link"
          >
            Mot de passe oublié ?
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}

// ============== Activate (via lien invitation) ==============
export function CorporatePortalActivate() {
  const { token } = useParams();
  const { activate } = useCorporateAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    setSubmitting(true);
    try {
      await activate(token, password);
      navigate("/portail-corporate", { replace: true });
    } catch (err) {
      setError(formatCorpErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Activer votre accès"
      subtitle="Choisissez le mot de passe qui protégera votre espace Corporate."
      testid="corp-activate-page"
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="corp-activate-form">
        <div>
          <Label htmlFor="pwd" className="mb-1.5 block text-[#1A1C1A]">Nouveau mot de passe</Label>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#575A57] pointer-events-none" />
            <Input
              id="pwd" type="password" autoComplete="new-password" minLength={8}
              value={password} onChange={(e) => setPassword(e.target.value)}
              required className="pl-9"
              data-testid="corp-activate-password"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="cf" className="mb-1.5 block text-[#1A1C1A]">Confirmer</Label>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#575A57] pointer-events-none" />
            <Input
              id="cf" type="password" autoComplete="new-password" minLength={8}
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              required className="pl-9"
              data-testid="corp-activate-confirm"
            />
          </div>
        </div>
        {error && <ErrorBox testid="corp-activate-error">{error}</ErrorBox>}
        <Button
          type="submit" disabled={submitting || !password || !confirm}
          className="w-full bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
          data-testid="corp-activate-submit"
        >
          {submitting ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Activation…</>
          ) : "Activer et se connecter"}
        </Button>
      </form>
    </AuthShell>
  );
}

// ============== Forgot Password ==============
export function CorporatePortalForgot() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await corporateApi.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatCorpErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <AuthShell title="Email envoyé" subtitle="Si un compte existe pour cette adresse, un lien vient d'être envoyé." testid="corp-forgot-sent">
        <div className="text-sm text-[#575A57]">
          Vérifiez votre boîte de réception (et vos spams) — le lien expire dans 1&nbsp;heure.
        </div>
        <div className="mt-6 text-center">
          <Link to="/portail-corporate/login" className="text-sm underline text-[#1F4E3D]" data-testid="corp-forgot-back-login">
            ← Retour à la connexion
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Mot de passe oublié" subtitle="Entrez votre email — nous vous envoyons un lien de réinitialisation." testid="corp-forgot-page">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="corp-forgot-form">
        <div>
          <Label htmlFor="fe" className="mb-1.5 block text-[#1A1C1A]">Adresse email</Label>
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#575A57] pointer-events-none" />
            <Input
              id="fe" type="email" autoComplete="username"
              value={email} onChange={(e) => setEmail(e.target.value)}
              required className="pl-9"
              data-testid="corp-forgot-email"
            />
          </div>
        </div>
        {error && <ErrorBox testid="corp-forgot-error">{error}</ErrorBox>}
        <Button
          type="submit" disabled={submitting || !email}
          className="w-full bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
          data-testid="corp-forgot-submit"
        >
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi…</> : "Envoyer le lien"}
        </Button>
        <div className="text-center pt-2">
          <Link to="/portail-corporate/login" className="text-sm text-[#575A57] hover:text-[#1F4E3D] underline">
            ← Retour à la connexion
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}

// ============== Reset Password (via token) ==============
export function CorporatePortalReset() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    setSubmitting(true);
    try {
      await corporateApi.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/portail-corporate/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatCorpErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthShell title="Mot de passe modifié" subtitle="Vous allez être redirigé vers la connexion…" testid="corp-reset-done">
        <div className="text-center">
          <CheckCircle2 className="w-12 h-12 text-[#1F4E3D] mx-auto" />
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Nouveau mot de passe" subtitle="Choisissez un mot de passe (8 caractères min.)." testid="corp-reset-page">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="corp-reset-form">
        <div>
          <Label htmlFor="rp" className="mb-1.5 block text-[#1A1C1A]">Nouveau mot de passe</Label>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#575A57] pointer-events-none" />
            <Input
              id="rp" type="password" autoComplete="new-password" minLength={8}
              value={password} onChange={(e) => setPassword(e.target.value)}
              required className="pl-9"
              data-testid="corp-reset-password"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="rc" className="mb-1.5 block text-[#1A1C1A]">Confirmer</Label>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#575A57] pointer-events-none" />
            <Input
              id="rc" type="password" autoComplete="new-password" minLength={8}
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              required className="pl-9"
              data-testid="corp-reset-confirm"
            />
          </div>
        </div>
        {error && <ErrorBox testid="corp-reset-error">{error}</ErrorBox>}
        <Button
          type="submit" disabled={submitting || !password || !confirm}
          className="w-full bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
          data-testid="corp-reset-submit"
        >
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement…</> : "Réinitialiser"}
        </Button>
      </form>
    </AuthShell>
  );
}

// ============== Dashboard Portail (auth required) ==============
export function CorporatePortal() {
  const { contact, logout } = useCorporateAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!contact || contact === false) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await corporateApi.get("/portal");
        setData(data);
        setError(null);
      } catch (err) {
        setError(formatCorpErrorDetail(err?.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [contact]);

  if (contact === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F0]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1F4E3D]" />
      </div>
    );
  }
  if (contact === false) {
    return <Navigate to="/portail-corporate/login" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F0]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1F4E3D]" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F0] p-4">
        <div className="text-center">
          <div className="text-lg font-semibold text-[#8B3D2D] mb-2">Espace indisponible</div>
          <p className="text-sm text-[#575A57] mb-4">{error || "Aucune donnée"}</p>
          <Button variant="outline" onClick={logout}>Se déconnecter</Button>
        </div>
      </div>
    );
  }

  const menu = data.menu || {};
  const revenue = data.revenue || {};
  const grouped = {};
  for (const it of menu.items || []) {
    const key = it.category || "Autres";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(it);
  }
  const isBelow = revenue.status === "BELOW";
  const guarantee = Number(revenue.guarantee_chf || 0);
  const rev = Number(revenue.revenue_chf || 0);
  const pct = guarantee > 0 ? Math.min(100, (rev / guarantee) * 100) : 100;

  return (
    <div className="min-h-screen bg-[#F5F5F0]" data-testid="corp-portal">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5E0] shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#1F4E3D] flex items-center justify-center text-white shadow-sm">
              <Leaf className="w-4 h-4" />
            </div>
            <div>
              <div className="font-heading text-lg font-medium text-[#1A1C1A]">Espace Corporate</div>
              <div className="text-xs text-[#575A57]">
                <b>{data.distributor?.name}</b>
                {data.distributor?.city ? ` · ${data.distributor.city}` : ""}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm text-[#575A57] hidden sm:block" data-testid="corp-portal-contact-name">
              {contact.name} {contact.role ? <span className="text-[#A3A6A3]">· {contact.role}</span> : null}
            </div>
            <Button
              variant="outline" size="sm"
              onClick={logout}
              className="text-[#575A57]"
              data-testid="corp-portal-logout"
            >
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> Se déconnecter
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Card 1 : CA mensuel */}
        <section
          className="bg-white border border-[#C9B5E0] rounded-xl p-6 shadow-sm"
          data-testid="corp-portal-revenue"
        >
          <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#5B3A8C]" />
              <h2 className="font-heading text-lg font-medium text-[#3D2A5C]">
                Chiffre d&apos;affaires — {revenue.month}
              </h2>
            </div>
            <div
              className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                isBelow
                  ? "bg-[#FBEEEA] text-[#A04E2C] border border-[#E8B0A0]"
                  : "bg-[#E8F2EC] text-[#1F4E3D] border border-[#A8C9B6]"
              }`}
              data-testid="corp-portal-revenue-status"
            >
              {isBelow ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
              {isBelow ? "Sous garantie" : guarantee === 0 ? "Pas de garantie" : "OK"}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <KpiCell label="CA réalisé" value={fmtCHF(rev)} sub={`${revenue.units_sold || 0} barquettes · ${revenue.sales_count || 0} ventes`} />
            <KpiCell label="CA garanti" value={guarantee > 0 ? fmtCHF(guarantee) : "—"} sub={guarantee > 0 ? "Minimum mensuel négocié" : "Non défini"} />
            <KpiCell
              label="Participation à verser"
              value={fmtCHF(revenue.participation_ht_chf || revenue.delta_to_invoice_chf)}
              sub={
                revenue.uses_tiers
                  ? "Tranche du mois (HT)"
                  : revenue.delta_to_invoice_chf > 0
                  ? "HT (différence à combler)"
                  : "Rien à verser"
              }
              accent={Number(revenue.participation_ht_chf || revenue.delta_to_invoice_chf || 0) > 0}
            />
          </div>
          {guarantee > 0 && (
            <div
              className="h-2 bg-[#F5F5F0] rounded-full overflow-hidden border border-[#DBC9EE]"
              data-testid="corp-portal-progress"
            >
              <div
                className={`h-full transition-all duration-500 ${
                  isBelow ? "bg-[#C05C47]" : "bg-[#1F4E3D]"
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
          )}
          {Number(revenue.tva_rate || 0) > 0 && Number(revenue.participation_ht_chf || 0) > 0 && (
            <div className="mt-3 text-xs text-[#575A57]">
              TVA {revenue.tva_rate}% ({fmtCHF(revenue.tva_amount_chf)}) — Total TTC : <b>{fmtCHF(revenue.participation_ttc_chf)}</b>
            </div>
          )}
        </section>

        {/* Phase 101du.3 — Historique participations facturées */}
        <ParticipationHistorySection />

        {/* Card 2 : Menu employés */}
        <section
          className="bg-white border border-[#E5E5E0] rounded-xl p-6 shadow-sm"
          data-testid="corp-portal-menu"
        >
          <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
            <h2 className="font-heading text-lg font-medium text-[#1A1C1A]">Prix employés — catalogue Maison Thaï</h2>
            <div className="text-xs text-[#575A57]" data-testid="corp-portal-counter">
              <span className="font-semibold text-[#1F4E3D]">{menu.count_negotiated || 0}</span>
              {" "}prix négociés / {menu.count_total || 0} au catalogue
            </div>
          </div>
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category} className="mb-5">
              {category && category !== "Autres" && (
                <div className="text-xs font-semibold uppercase tracking-wider text-[#8C6B2F] mb-1.5">
                  {category}
                </div>
              )}
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-xs text-[#575A57] border-b border-[#E5E5E0]">
                    <th className="text-left py-1.5 font-medium">Produit</th>
                    <th className="text-right py-1.5 font-medium w-32 text-[#1F4E3D]">Prix employé</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr
                      key={it.product_id}
                      className={`border-b border-[#F0F0EB] ${!it.has_override ? "opacity-70" : ""}`}
                    >
                      <td className="py-1.5">
                        <div className="font-medium text-[#1A1C1A] flex items-center gap-1.5 flex-wrap">
                          {it.name}
                          {it.is_current_on_machine && (
                            <span className="inline-block text-[9px] uppercase tracking-wider text-[#1F4E3D] bg-[#E8F2EC] border border-[#A8C9B6] rounded px-1.5 py-0.5 font-semibold">
                              Sur machine
                            </span>
                          )}
                        </div>
                        {it.portion_size && (
                          <div className="text-[10px] text-[#A3A6A3]">{it.portion_size} g</div>
                        )}
                      </td>
                      <td className="py-1.5 text-right font-mono font-semibold text-[#1F4E3D]">
                        {it.has_override ? (
                          <>
                            {fmtCHF(it.price_employee)}
                            <span className="ml-1 text-[9px] text-[#8C6B2F] font-normal">★</span>
                          </>
                        ) : (
                          <span className="italic text-[#A3A6A3] font-normal text-xs">à définir</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <p className="text-[10px] text-[#A3A6A3] italic mt-3">
            ★ = Prix négocié dans votre convention. Les plats « à définir » sont au catalogue
            Maison Thaï — leur tarif employé sera fixé selon la marge, dans le cadre de votre contrat.
          </p>
        </section>

        {/* Card 3 : Lien public à partager */}
        <section
          className="bg-[#FBF4E9] border border-[#E5DBC1] rounded-xl p-5 shadow-sm flex items-center justify-between gap-4 flex-wrap"
          data-testid="corp-portal-share-link"
        >
          <div>
            <div className="font-medium text-[#1A1C1A]">Partager le menu à vos employés</div>
            <div className="text-xs text-[#575A57] mt-0.5">
              Un lien public imprimable, sans connexion, sans données CA.
            </div>
          </div>
          <a
            href={`/corporate/${data.distributor?.id}`}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-[#8C6B2F] underline hover:text-[#6F531F] flex items-center gap-1.5"
            data-testid="corp-portal-share-open"
          >
            Ouvrir le menu public <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </section>
      </main>
    </div>
  );
}

function KpiCell({ label, value, sub, accent = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#5B3A8C] mb-0.5">{label}</div>
      <div className={`text-xl font-mono font-semibold ${accent ? "text-[#A04E2C]" : "text-[#1A1C1A]"}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-[#575A57] mt-0.5">{sub}</div>}
    </div>
  );
}

// ============== Phase 101du.3 — Historique des participations facturées ==============
function ParticipationHistorySection() {
  const [state, setState] = useState({ loading: true, items: [], summary: null, error: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await corporateApi.get("/portal/participation-history");
        setState({ loading: false, items: data.items || [], summary: data.summary || null, error: null });
      } catch (err) {
        setState({ loading: false, items: [], summary: null, error: formatCorpErrorDetail(err?.response?.data?.detail) || err.message });
      }
    })();
  }, []);

  if (state.loading) {
    return (
      <section className="bg-white border border-[#E5E5E0] rounded-xl p-6 shadow-sm" data-testid="corp-portal-participation">
        <div className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-[#5B3A8C]" /><span className="text-sm text-[#575A57]">Chargement de l&apos;historique…</span></div>
      </section>
    );
  }

  if (state.error) {
    return (
      <section className="bg-[#FCEEEA] border border-[#C05C47]/30 rounded-xl p-4 text-sm text-[#8B3D2D]" data-testid="corp-portal-participation-error">
        Historique indisponible : {state.error}
      </section>
    );
  }

  if (state.items.length === 0) {
    return null; // Rien à afficher — évite du bruit UX
  }

  const statusBadge = (status) => {
    const map = {
      FINALIZED: { bg: "#FBF4E9", fg: "#8C6B2F", label: "À envoyer", border: "#E8D3A8" },
      SENT: { bg: "#FBEEEA", fg: "#A04E2C", label: "En attente paiement", border: "#E8B0A0" },
      PAID: { bg: "#E8F2EC", fg: "#1F4E3D", label: "Payée", border: "#A8C9B6" },
    };
    const s = map[status] || map.FINALIZED;
    return (
      <span
        className="inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border"
        style={{ background: s.bg, color: s.fg, borderColor: s.border }}
      >
        {s.label}
      </span>
    );
  };

  return (
    <section
      className="bg-white border border-[#E5E5E0] rounded-xl p-6 shadow-sm"
      data-testid="corp-portal-participation"
    >
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#5B3A8C]" />
          <h2 className="font-heading text-lg font-medium text-[#1A1C1A]">Historique des participations facturées</h2>
        </div>
        {state.summary && state.summary.count_open > 0 && (
          <div
            className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#FBEEEA] text-[#A04E2C] border border-[#E8B0A0]"
            data-testid="corp-portal-participation-summary"
          >
            <AlertCircle className="w-3 h-3" />
            {state.summary.count_open} ouverte{state.summary.count_open > 1 ? "s" : ""} · {fmtCHF(state.summary.total_open_chf)}
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="corp-portal-participation-table">
          <thead>
            <tr className="text-xs text-[#575A57] border-b border-[#E5E5E0]">
              <th className="text-left py-2 font-medium">Période</th>
              <th className="text-left py-2 font-medium">CA du mois</th>
              <th className="text-left py-2 font-medium">Palier</th>
              <th className="text-right py-2 font-medium text-[#1F4E3D]">Participation TTC</th>
              <th className="text-left py-2 font-medium">Statut</th>
              <th className="text-left py-2 font-medium">Facture</th>
            </tr>
          </thead>
          <tbody>
            {state.items.map((it) => (
              <tr
                key={it.id}
                className="border-b border-[#F0F0EB]"
                data-testid={`corp-portal-participation-row-${it.year}-${it.month}`}
              >
                <td className="py-2 font-medium text-[#1A1C1A]">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-[#8C6B2F]" />
                    {it.period_label}
                  </div>
                </td>
                <td className="py-2 font-mono text-[#575A57]">{fmtCHF(it.revenue_chf)}</td>
                <td className="py-2 text-xs text-[#575A57]">
                  {it.matched_tier
                    ? (it.matched_tier.ca_max
                        ? `${it.matched_tier.ca_min}–${it.matched_tier.ca_max} CHF`
                        : `≥ ${it.matched_tier.ca_min} CHF`)
                    : "—"}
                </td>
                <td className="py-2 text-right font-mono font-semibold text-[#A04E2C]">
                  {fmtCHF(it.participation_ttc_chf)}
                </td>
                <td className="py-2">{statusBadge(it.status)}</td>
                <td className="py-2 text-xs font-mono text-[#8C6B2F]">
                  {it.invoice_reference ? (
                    <span title={it.invoice_due_date ? `Échéance ${it.invoice_due_date}` : ""}>{it.invoice_reference}</span>
                  ) : (
                    <span className="italic text-[#A3A6A3]">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-[#A3A6A3] italic mt-3">
        Les factures Swiss QR-Bill sont envoyées à l&apos;adresse de facturation de votre société.
        En cas de retard ou de question, répondez directement à l&apos;email reçu.
      </p>
    </section>
  );
}
